// Admin menus — /api/admin/menus (System A: menu tree · System B: entity_type mid sections)

export type MenuMidSection =
  | 'main'
  | 'market'
  | 'home'
  | 'place'
  | 'event'
  | 'experience'
  | 'service'
  | 'vehicle'
  | 'free'
  | 'prim'
  | 'sec';

export interface MenuNode {
  menid: number;
  uuid?: string;
  menid_root?: number | null;
  menid_parent?: number | null;
  name: string;
  name_system?: string | null;
  description?: string | null;
  order_by?: number | null;
  locale?: string | null;
  active?: number | boolean;
  cmsoid?: number | null;
  ext_link?: string | null;
  etid?: number | null;
  etid_root?: number | null;
  eid?: number | null;
  uid?: number | null;
  oid?: number | null;
  date_added?: string;
  date_modified?: string;
  [key: string]: unknown;
}

export interface MenuTreeNode extends MenuNode {
  children?: MenuTreeNode[];
}

export interface MenuRoot extends MenuNode {
  node_count?: number;
}

export interface CreateMenuNodePayload {
  name: string;
  menid_root?: number | null;
  menid_parent?: number | null;
  order_by?: number | null;
  locale?: string | null;
  description?: string | null;
  name_system?: string | null;
  cmsoid?: number | null;
  ext_link?: string | null;
  etid?: number | null;
  etid_root?: number | null;
  active?: number | boolean;
}

export interface UpdateMenuNodePayload extends Partial<CreateMenuNodePayload> {
  menid: number;
}

export interface MoveMenuNodePayload {
  menid: number;
  menid_parent?: number | null;
  menid_root?: number | null;
  order_by?: number | null;
}

export interface MenuReorderItem {
  menid: number;
  order_by: number;
}

export interface MenuSectionSummary {
  section: MenuMidSection | string;
  flag_column: string;
  count: number;
}

export interface MenuSectionMember {
  etid: number;
  name_menu?: string | null;
  name_friendly?: string | null;
  name?: string | null;
  order_by?: number | null;
  menu_mid_main_order?: number | null;
  active?: boolean | number;
  menu_prim?: boolean | number;
  menu_sec?: boolean | number;
  menu_sec_menid?: number | null;
  [key: string]: unknown;
}

export interface MenuSectionMembersResult {
  section: string;
  flag_column: string;
  members: MenuSectionMember[];
}

export interface MenuSectionReorderItem {
  etid: number;
  order: number;
}

export const MENU_SECTION_LABELS: Record<string, string> = {
  main: 'Homepage strip',
  market: 'Market page strip',
  home: 'Homes page strip',
  place: 'Locals / Places strip',
  event: 'Events strip',
  experience: 'Experiences strip',
  service: 'Services strip',
  vehicle: 'Vehicles / Cars strip',
  free: 'Free / giveaway strip',
  prim: 'Primary flag (advanced)',
  sec: 'Secondary flag (advanced)',
};

/** Where each mid section appears on the public site. */
export const MENU_SECTION_SITE_HINTS: Record<string, string> = {
  main: 'Scrollable chips on the homepage (/)',
  market: 'Category strip on /market (and /landing)',
  home: 'Category strip on /homes',
  place: 'Category strip on /locals and /places',
  event: 'Events vertical',
  experience: 'Category strip on /experiences',
  service: 'Services vertical',
  vehicle: 'Category strip on /cars',
  free: 'Category strip on free listings',
  prim: 'entity_type.menu_prim — not a visible page strip',
  sec: 'entity_type.menu_sec membership list — prefer Header categories tab',
};

/** Mid sections staff usually edit (page strips), in display order. */
export const MENU_SECTION_PRIMARY_ORDER = [
  'main',
  'market',
  'home',
  'place',
  'vehicle',
  'experience',
  'free',
  'event',
  'service',
] as const;

export const MENU_SECTION_ADVANCED = new Set(['prim', 'sec']);

export const MENU_SECTIONS_WITH_DEDICATED_ORDER = new Set(['main']);

// ─── Secondary category bar (per pillar) ──────────────────────────────────────

export type MenuSecPillar = 'market' | 'homes' | 'places' | 'locals' | 'experiences' | string;

export interface MenuSecHierarchyNode {
  etid: number;
  name_menu?: string | null;
  name_friendly?: string | null;
  name?: string | null;
  order_by?: number | null;
  menu_sec?: boolean | number;
  menu_sec_menid?: number | null;
  active?: boolean | number;
  /** L2 subtypes (or empty when a menu bridge is used). */
  children?: MenuSecHierarchyNode[];
  /** Present when menu_sec_menid bridges into a System-A subtree. */
  menu?: MenuNode[];
  [key: string]: unknown;
}

export interface MenuSecBarResult {
  pillar: string;
  etid_root: number;
  hierarchy: MenuSecHierarchyNode;
}

/** Pillars as shown in the public header (Market · Homes · Locals). */
export const MENU_SEC_PILLARS: {
  id: string;
  label: string;
  etid_root: number;
  /** Label in the white primary nav row */
  primaryNavLabel: string;
  inPrimaryNav: boolean;
  hint: string;
}[] = [
  {
    id: 'market',
    label: 'Market',
    etid_root: 32,
    primaryNavLabel: 'Market',
    inPrimaryNav: true,
    hint: 'Blue bar under Market — goods & search categories',
  },
  {
    id: 'homes',
    label: 'Homes',
    etid_root: 2,
    primaryNavLabel: 'Homes',
    inPrimaryNav: true,
    hint: 'Blue bar under Homes — property categories',
  },
  {
    id: 'places',
    label: 'Locals',
    etid_root: 21,
    primaryNavLabel: 'Locals',
    inPrimaryNav: true,
    hint: 'Blue bar under Locals (Places tree, etid 21)',
  },
  {
    id: 'experiences',
    label: 'Experiences',
    etid_root: 4638,
    primaryNavLabel: 'Experiences',
    inPrimaryNav: false,
    hint: 'Hierarchy exists but is not in the primary nav yet',
  },
];

/** Footer columns loaded by the public site via /public/menu/ids */
export const FOOTER_MENU_ROOTS: { menid: number; label: string }[] = [
  { menid: 1, label: 'Company' },
  { menid: 11, label: 'Market' },
  { menid: 21, label: 'Experience' },
  { menid: 26, label: "Let's do business" },
  { menid: 31, label: 'Social Media' },
  { menid: 100, label: 'Legal & policies' },
];

export const FOOTER_MENU_ROOT_IDS = new Set(FOOTER_MENU_ROOTS.map((r) => r.menid));
